import React, { FC } from 'react';
import { buildImageSource, buildImageSourceSet } from '../../../../utils/buildImageSource';
import { formatCurrency } from '../../../../utils/format/formatCurrency';
import { useTranslation } from '../../../../utils/useTranslation';
import { IconProduct } from '../../../icon/IconProduct';
import { IconProductOutline } from '../../../icon/IconProductOutline';
import { Flex } from '../../../layout/Flex';
import { Image } from '../../../media/Image';
import { Retry } from '../../../media/Retry';
import { Typography } from '../../../typography/Typography';
import { ProductMonetizationPlainText } from '../../ProductMonetizationPlainText/ProductMonetizationPlainText';
import { PostUI } from '../../types';
import { ProductMediaCover, ProductTextContainer, ProductTitle } from './styles';

interface ProductViewProps {
    products: PostUI['products'];
}

const MAX_PRODUCTS_PICTURES = 5;

export const ProductView: FC<ProductViewProps> = ({ products }) => {
    const t = useTranslation();

    if (!products.items.length) {
        return null;
    }

    if (products.items.length === 1) {
        const { priceInfo, content, title, monetizationEnabled } = products.items[0];

        const media = content[0]?.media;
        const integerPart = priceInfo?.integerPart;
        const fractionalPart = priceInfo?.fractionalPart;
        const hasPriceInfo = integerPart !== undefined && integerPart !== null;

        return (
            <>
                {media ? (
                    <ProductMediaCover>
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            padding={0.5}
                            backgroundColor="white"
                        >
                            <Retry>
                                <Image
                                    width="100%"
                                    height="100%"
                                    src={buildImageSource(media)}
                                    srcSet={buildImageSourceSet(media)}
                                />
                            </Retry>
                        </Flex>
                    </ProductMediaCover>
                ) : (
                    <ProductMediaCover backgroundColor="transparent" boxShadow="none">
                        <Flex alignItems="center" justifyContent="center">
                            <IconProductOutline size={24} color="var(--always-black)" />
                        </Flex>
                    </ProductMediaCover>
                )}
                <ProductTextContainer>
                    {monetizationEnabled ? (
                        <ProductMonetizationPlainText
                            color="white"
                            textTransform="none"
                            pb={0.25}
                        />
                    ) : null}
                    <ProductTitle>{t(title)}</ProductTitle>
                    {hasPriceInfo ? (
                        <Typography
                            weight="medium"
                            variant="caption"
                            color="black"
                            textTransform="none"
                        >
                            {formatCurrency(integerPart, fractionalPart)}
                        </Typography>
                    ) : null}
                </ProductTextContainer>
            </>
        );
    }

    const productsToView =
        products.items.length > MAX_PRODUCTS_PICTURES
            ? products.items.slice(0, MAX_PRODUCTS_PICTURES)
            : products.items;

    return (
        <>
            <>
                {productsToView.map((product, index) => {
                    const media = product.content[0]?.media;

                    return (
                        <ProductMediaCover index={index} key={index}>
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                padding={0.5}
                                backgroundColor="white"
                            >
                                {media ? (
                                    <Retry>
                                        <Image
                                            width="100%"
                                            height="100%"
                                            src={buildImageSource(media)}
                                            srcSet={buildImageSourceSet(media)}
                                        />
                                    </Retry>
                                ) : (
                                    <IconProduct size={36} color="rgba(0, 0, 0, 0.12)" />
                                )}
                            </Flex>
                        </ProductMediaCover>
                    );
                })}
            </>
            <ProductTextContainer indexOffset={productsToView.length - 1}>
                <ProductTitle>{t(products.title)}</ProductTitle>
                {products.items.some((product) => product.monetizationEnabled) ? (
                    <ProductMonetizationPlainText color="white" textTransform="none" />
                ) : (
                    <Typography
                        weight="medium"
                        variant="caption"
                        color="black"
                        textTransform="none"
                    >
                        {t(products.description)}
                    </Typography>
                )}
            </ProductTextContainer>
        </>
    );
};
